import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Folder,
  RefreshCw,
  LoaderCircle,
  GripVertical,
  Check,
  Filter,
  ChevronDown,
  ChevronRight,
  EyeOff,
  Ban,
  RotateCcw,
} from 'lucide-react';
import { api } from '@/lib/api';
import { friendlyStorageError } from '@/lib/storageErrorCopy';
import { loadFreDraft, saveFreDraft, clearFreDraft, selectDraftForMode, isFreFormMeaningful } from '@/lib/freDraft';
import { useProject } from '@/lib/ProjectContext';
import { Telemetry } from '@/lib/telemetry';
import { listChildFolders } from '@/lib/fileAccess';
import { isSPOMode } from '@/design/tokens';
import { SPOFilePicker } from '@/components/shared/SPOFilePicker';
import { FolderTagInput } from '@/components/shared/FolderTagInput';
import {
  annotateFolders,
  addIgnoreName,
  removeIgnoreName,
  mergeActiveOrder,
} from './folderClassification';
import { useScanTask } from '@/lib/useScanTask';
import { ScanProgress } from '@/components/shared/ScanProgress';
import type { ChildFolder } from '@/lib/fileAccess';
import type { SPOSelection } from '@/lib/graphApi';
import type { IngestResult, Project } from '@/types/api';

interface SubmissionFolder {
  id: string;
  folderName: string;
  path: string;
}

export function FrePage() {
  const { reload, setActiveProject } = useProject();
  // A2F — restore an in-progress draft once (lazy initializer evaluates only on
  // first render) so a SharePoint/Azure reconnect or navigate-away-and-back
  // doesn't erase what the user typed. A draft authored in the other storage
  // mode is discarded rather than half-applied (the empty-form save effect below
  // then clears the stale blob). null when there is no live, same-mode draft.
  const [initialDraft] = useState(() => selectDraftForMode(loadFreDraft(), isSPOMode()));
  const [projectName, setProjectName] = useState(initialDraft?.projectName ?? '');
  const [indNumber, setIndNumber] = useState(initialDraft?.indNumber ?? '');
  const [rootPath, setRootPath] = useState(initialDraft?.rootPath ?? '');
  const [error, setError] = useState('');
  // Advanced settings — folder rules (A2F-477 + A2F-1035 glob).
  // ignoreFolders is seeded from GET /folder-filter-defaults on mount so FRE
  // mirrors Manage Project (the convention defaults are visible, editable
  // chips, not a post-ingest surprise). systemCore is the always-on, locked set.
  const [rulesExpanded, setRulesExpanded] = useState(initialDraft?.rulesExpanded ?? false);
  const [ignoreFolders, setIgnoreFolders] = useState<string[]>(initialDraft?.ignoreFolders ?? []);
  const [activeFolders, setActiveFolders] = useState<string[]>(initialDraft?.activeFolders ?? []);
  const [systemCore, setSystemCore] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [ingesting, setIngesting] = useState(false);
  const createdProjectRef = useRef<Project | null>(null);

  const { taskState, startTask, cancel: cancelIngest } = useScanTask<IngestResult>(
    undefined,
    () => {
      // onComplete — fire telemetry and redirect
      Telemetry.projectCreated({ has_ind_number: indNumber.trim().length > 0 });
      if (ignoreFolders.length > 0 || activeFolders.length > 0) {
        Telemetry.projectFolderRulesConfigured({
          ignore_count: ignoreFolders.length,
          active_count: activeFolders.length,
          surface: 'project_create',
        });
      }
      Telemetry.freCompleted({ submission_count: activeSubmissions.length, document_count: 0 });
      if (createdProjectRef.current) {
        setActiveProject(createdProjectRef.current);
      }
      // Project created — drop the saved draft so the next Add New IND starts clean.
      clearFreDraft();
      window.location.replace('/');
    },
  );

  const handleRetryIngest = async () => {
    if (!createdProjectRef.current) return;
    try {
      const { taskId } = await api.projects.ingest(createdProjectRef.current.id);
      startTask(taskId, activeSubmissions.length);
    } catch (err) {
      setError(friendlyStorageError(err, 'Retry failed'));
      setIngesting(false);
    }
  };

  const handleDismissIngest = () => {
    setIngesting(false);
    setError('Ingestion was not completed. You can retry from Settings → Manage Project.');
  };

  // SPO selection state
  const [spoSelection, setSpoSelection] = useState<SPOSelection | null>(initialDraft?.spoSelection ?? null);

  // Folder discovery
  const [folders, setFolders] = useState<SubmissionFolder[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  // A2F-1035 — seed the Ignore list + locked system core from the
  // project-agnostic defaults endpoint (FRE has no project id yet). Seed
  // ignoreFolders ONCE so the user's edits (removing a default, adding their
  // own) are never clobbered by a re-fetch; systemCore is the locked set passed
  // to the Ignore FolderTagInput. Failure is silent — onboarding still works
  // with an empty ignore list, it just won't pre-fill the recommended chips.
  // When a draft was restored AND its ignore-defaults had already been seeded at
  // save time, treat ignoreFolders as seeded so the defaults fetch below sets
  // only systemCore (the locked set) and never overwrites the user's restored
  // chips. A draft saved before the defaults loaded (seeded:false) intentionally
  // does NOT suppress seeding, so the recommended chips still appear on restore
  // — its restored ignoreFolders is replaced by the defaults, acceptable because
  // the user couldn't have curated chips before the defaults had loaded.
  const seededRef = useRef(initialDraft?.seeded === true);
  useEffect(() => {
    let cancelled = false;
    api.projects.folderFilterDefaults()
      .then((d) => {
        if (cancelled) return;
        setSystemCore(d.systemCore);
        if (!seededRef.current) {
          seededRef.current = true;
          setIgnoreFolders(d.defaultIgnoreGlobs);
        }
      })
      .catch(() => { /* non-fatal — leave ignore list empty */ });
    return () => { cancelled = true; };
  }, []);

  /**
   * Auto-discover child folders when root path changes.
   * Directly reuses the INDx prototype pattern: list child dirs, show as cards.
   */
  const discoverFolders = useCallback(async (path: string) => {
    if (!path.trim()) {
      setFolders([]);
      return;
    }

    setLoadingFolders(true);
    setError('');

    try {
      const childFolders = await listChildFolders(path.trim());

      // Transform to submission format (oldest first = index 0)
      const submissions: SubmissionFolder[] = childFolders.map(
        (f: ChildFolder, i: number) => ({
          id: `folder-${i}-${Date.now()}`,
          folderName: f.name,
          path: f.path,
        }),
      );

      // Reverse for display: newest first (N, N-1, ..., 1)
      setFolders(submissions.reverse());
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to list folders. Is the backend running?',
      );
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  // Auto-scan when rootPath changes (debounced)
  useEffect(() => {
    if (!rootPath.trim()) {
      setFolders([]);
      return;
    }
    const timer = setTimeout(() => discoverFolders(rootPath), 400);
    return () => clearTimeout(timer);
  }, [rootPath, discoverFolders]);

  // A2F-1035 — the full ignore-glob set ingest will actually apply: the locked
  // system core plus the user's current (seeded, then edited) ignore chips.
  // This is exactly what the create payload sends with ignore_seeded:true, so
  // "what you see ignored here = what ingest skips".
  const effectiveIgnoreGlobs = useMemo(
    () => [...systemCore, ...ignoreFolders],
    [systemCore, ignoreFolders],
  );

  // Reactively annotate the discovered (newest-first) folders so they render in
  // ONE ordered list, mirroring Manage Project's "Show hidden" view. Re-runs
  // whenever the user edits the Ignore list (Advanced Settings) or clicks
  // Disable/Enable, so the list re-marks live.
  //
  // Each folder is one of:
  //   - active      → not matched by any effective ignore glob. Numbered
  //                    "Submission N" (contiguous over active only), draggable,
  //                    with a Disable action.
  //   - disabled    → excluded AND its exact (casefolded) name is one of the
  //                    user's Ignore chips, AND removing those exact chips would
  //                    un-exclude it. The Manage-Project `is_disabled` analog:
  //                    "Disabled" badge + reversible Enable. Covers both a click
  //                    of Disable and an exact seeded default (e.g. `archive`).
  //   - rule        → excluded by a wildcard pattern (`*pec*`) or by system core
  //                    (`.*` / `~$*`), i.e. removing the exact-name chips would
  //                    NOT un-exclude it. The "Removed from source" analog: an
  //                    "Ignored by rule" badge, no per-folder toggle.
  // The classifier itself lives in ./folderClassification (pure + unit-tested).
  const annotatedFolders = useMemo(
    () => annotateFolders(folders, effectiveIgnoreGlobs),
    [folders, effectiveIgnoreGlobs],
  );

  // The numbered (ingested) subset — everything not excluded. Drives the
  // submission count, the progress-bar size, the create gate, and reorder.
  const activeSubmissions = useMemo(
    () => annotatedFolders.filter((a) => a.state === 'active').map((a) => a.folder),
    [annotatedFolders],
  );

  /** Discover the top-level folders under an SPO selection as submissions. */
  const discoverSPOFolders = useCallback(async (selection: SPOSelection) => {
    setError('');
    setLoadingFolders(true);

    try {
      // A2F-423: discover submission folders via the backend (org app-only
      // creds), not the browser's delegated Graph token.
      const parentId = selection.folderId;
      const { folders: items } = await api.projects.spo.folders(selection.driveId, parentId);
      const submissions: SubmissionFolder[] = items.map((item) => ({
        id: `spo-${item.id}`,
        folderName: item.name,
        path: item.id, // SPO item ID as path
      }));
      // Reverse for display: newest first
      setFolders(submissions.reverse());
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to list SharePoint folders.',
      );
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  /** Handle SPO library selection — discover top-level folders as submissions. */
  const handleSPOSelect = (selection: SPOSelection) => {
    setSpoSelection(selection);
    discoverSPOFolders(selection);
  };

  // A2F — re-discover submissions for a draft-restored SPO selection. The folder
  // list isn't persisted (it's derived), so after a page restore we land back on
  // /fre with the library selected but no submissions; repopulate them. Guarded
  // on empty folders so it doesn't fight a fresh handleSPOSelect or a "Change"/clear.
  useEffect(() => {
    if (isSPOMode() && spoSelection && folders.length === 0) {
      discoverSPOFolders(spoSelection);
    }
    // Intentionally keyed only on the selection: folders.length is a guard, not
    // a trigger (re-running on every folder change would loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spoSelection]);

  // A2F — silently persist the in-progress form so the SharePoint/Azure reconnect
  // (a full-page OAuth redirect) doesn't erase it. Only save when the user has
  // entered something meaningful; an empty form clears the draft so abandoning or
  // wiping the form doesn't auto-refill next time. ignoreFolders is still
  // persisted (and triggers this effect) — it's only excluded from the
  // "meaningful" gate because it's pre-seeded with defaults and so isn't enough
  // on its own to indicate the user started filling the form.
  useEffect(() => {
    const draft = {
      projectName, indNumber, rootPath, spoSelection, ignoreFolders, activeFolders, rulesExpanded,
      seeded: seededRef.current,
      isSPO: isSPOMode(),
    };
    if (isFreFormMeaningful(draft)) {
      saveFreDraft(draft);
    } else {
      clearFreDraft();
    }
  }, [projectName, indNumber, rootPath, spoSelection, ignoreFolders, activeFolders, rulesExpanded]);

  // A2F — Disable mirrors Manage Project: instead of removing the folder from
  // the on-screen list (which had no effect — ingest re-discovers all folders
  // minus the ignore rules), add the folder's EXACT name to the ignore list.
  // effectiveIgnoreGlobs includes ignoreFolders, so the folder immediately greys
  // in place AND is genuinely skipped at ingest (it's in the list we send with
  // ignore_seeded:true). Dedupe case-insensitively, matching the backend.
  const handleDisableFolder = (folderName: string) => {
    setIgnoreFolders((prev) => addIgnoreName(prev, folderName));
  };

  // Enable: remove every ignore chip whose casefolded value equals the folder's
  // casefolded name (the exact-name chip that disabled it). System-core entries
  // and wildcard patterns are untouched, so a folder still caught by those stays
  // excluded — which is why only "disabled" rows expose this action.
  const handleEnableFolder = (folderName: string) => {
    setIgnoreFolders((prev) => removeIgnoreName(prev, folderName));
  };

  const handleSave = async () => {
    if (!projectName.trim()) {
      setError('Please enter a project name.');
      return;
    }
    if (isSPOMode() && !spoSelection) {
      setError('Please select a SharePoint document library.');
      return;
    }
    if (!isSPOMode() && !rootPath.trim()) {
      setError('Please select a project root folder.');
      return;
    }
    // Validate against the NUMBERED submissions (active = not excluded).
    // A root whose only child folders are all excluded has nothing to ingest,
    // so block create with a message that points at the per-folder Enable.
    if (activeSubmissions.length === 0) {
      setError(
        folders.length === 0
          ? 'No submission folders found. Check the root path.'
          : 'Every discovered folder is excluded, so nothing would be ingested. Enable a folder, or edit the Ignore Folders list under Advanced Settings, to include a submission.',
      );
      return;
    }

    setError('');
    setSaving(true);
    setSaveStatus('Creating project...');

    try {
      const createPayload: Parameters<typeof api.projects.create>[0] = {
        name: projectName.trim(),
        ind_number: indNumber.trim() || projectName.trim(),
      };

      if (isSPOMode() && spoSelection) {
        createPayload.spo_drive_id = spoSelection.driveId;
        createPayload.spo_folder_id = spoSelection.folderId;
        createPayload.spo_site_url = spoSelection.siteUrl;
      } else {
        createPayload.root_path = rootPath.trim();
      }

      // A2F-1035 — ALWAYS send folder_rules with ignore_seeded:true. This tells
      // the backend "these lists are the user's final set" so it stores them
      // verbatim and does NOT re-union the convention defaults. We send it even
      // when ignoreFolders is empty (the user cleared the seeded chips), so an
      // empty list is honored as a deliberate "ignore nothing" choice rather
      // than re-seeded.
      createPayload.folder_rules = {
        ignore_folder_names: ignoreFolders,
        active_folder_names: activeFolders,
        ignore_seeded: true,
      };

      const project = await api.projects.create(createPayload);
      createdProjectRef.current = project;

      // Start async ingest — switch to progress UI. Size the progress bar by the
      // numbered (active) submissions; rule-ignored folders aren't ingested.
      setSaving(false);
      setIngesting(true);
      const { taskId } = await api.projects.ingest(project.id);
      startTask(taskId, activeSubmissions.length);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Project already exists — just reload to pick it up
      if (msg.includes('409') || msg.includes('already exists')) {
        setSaveStatus('Project found! Loading...');
        clearFreDraft();
        await reload();
        window.location.replace('/');
        return;
      }
      setError(friendlyStorageError(err, msg));
      setSaving(false);
      setIngesting(false);
    }
  };

  // Drag-and-drop reordering
  const dragIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleDragStart = (idx: number) => {
    dragIdx.current = idx;
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };

  // idx values index into `activeSubmissions` (the rendered numbered list).
  // Reorder within the active subset, then stably merge back into `folders` so
  // rule-ignored folders keep their original relative positions (they're not
  // draggable and aren't part of the active sequence).
  const handleDrop = (idx: number) => {
    const from = dragIdx.current;
    if (from === null || from === idx) {
      dragIdx.current = null;
      setDragOverIdx(null);
      return;
    }
    const nextActive = [...activeSubmissions];
    const [moved] = nextActive.splice(from, 1);
    nextActive.splice(idx, 0, moved);
    // Stable-merge back into the full discovery order: active slots take the
    // reordered active rows, excluded rows stay pinned in place (./folderClassification).
    setFolders((prev) => mergeActiveOrder(prev, nextActive, effectiveIgnoreGlobs));
    dragIdx.current = null;
    setDragOverIdx(null);
  };

  const handleDragEnd = () => {
    dragIdx.current = null;
    setDragOverIdx(null);
  };

  // Save requires at least one NUMBERED submission (a root where every folder
  // is rule-ignored has nothing to ingest).
  const canSave = projectName.trim() && activeSubmissions.length > 0 &&
    (isSPOMode() ? !!spoSelection : !!rootPath.trim());

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-8">
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-brand-orange flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 18 18" fill="none">
                <rect x="1" y="1" width="7" height="7" rx="1.5" fill="white" />
                <rect x="10" y="1" width="7" height="7" rx="1.5" fill="white" opacity="0.6" />
                <rect x="1" y="10" width="7" height="7" rx="1.5" fill="white" opacity="0.6" />
                <rect x="10" y="10" width="7" height="7" rx="1.5" fill="white" opacity="0.3" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-brand-charcoal">CHRONA</h1>
              <p className="text-[10px] font-semibold text-brand-orange tracking-[0.15em] uppercase">
                Timely Biotech AI
              </p>
            </div>
          </div>
        </div>

        {/* Saving overlay — creating project */}
        {saving && !ingesting && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center space-y-4">
            <LoaderCircle className="w-10 h-10 text-brand-orange animate-spin mx-auto" />
            <h2 className="text-lg font-bold text-brand-charcoal">
              Setting Up Your Project
            </h2>
            <p className="text-sm text-slate-500">{saveStatus}</p>
          </div>
        )}

        {/* Ingestion progress (stays visible on error for retry/dismiss) */}
        {ingesting && (
          <div className="bg-white rounded-xl border border-slate-200 p-8">
            <h2 className="text-lg font-bold text-brand-charcoal text-center mb-2">
              Setting Up Your Project
            </h2>
            <ScanProgress
              state={taskState}
              onCancel={taskState.error ? handleDismissIngest : cancelIngest}
              onRetry={taskState.error ? handleRetryIngest : undefined}
              featureLabel="submissions"
              itemLabel="Submission"
            />
          </div>
        )}

        {/* Main form */}
        {!saving && !ingesting && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 space-y-6">
            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Project Name */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Project Name
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g., ABC-123"
                className="w-full px-4 py-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
              />
            </div>

            {/* IND Number */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                IND Number
              </label>
              <input
                type="text"
                value={indNumber}
                onChange={(e) => setIndNumber(e.target.value)}
                placeholder="e.g., 123456"
                className="w-full px-4 py-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
              />
            </div>

            {/* Document Source — SPO picker or local folder picker */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {isSPOMode() ? 'SharePoint Document Library' : 'Project Root Folder'}
              </label>

              {isSPOMode() ? (
                <>
                  {spoSelection ? (
                    <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
                      <Check className="w-5 h-5 text-green-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-brand-charcoal truncate">
                          {spoSelection.siteName} / {spoSelection.driveName}
                          {spoSelection.folderName && ` / ${spoSelection.folderName}`}
                        </p>
                        <p className="text-xs text-slate-400 truncate">{spoSelection.siteUrl}</p>
                      </div>
                      <button
                        onClick={() => {
                          setSpoSelection(null);
                          setFolders([]);
                        }}
                        className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-white transition-colors text-slate-500"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <SPOFilePicker onSelect={handleSPOSelect} />
                  )}
                </>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={rootPath}
                    onChange={(e) => setRootPath(e.target.value)}
                    placeholder="C:\\Lumen2\\IND12345"
                    className="flex-1 px-4 py-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange font-mono text-xs bg-white"
                  />
                  <button
                    onClick={() => discoverFolders(rootPath)}
                    disabled={loadingFolders || !rootPath.trim()}
                    className="px-4 py-3 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors font-medium text-slate-600 disabled:opacity-40 flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingFolders ? 'animate-spin' : ''}`} />
                    Reload
                  </button>
                </div>
              )}
            </div>

            {/* Submission Folders */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  Submission Folders
                </label>
                {activeSubmissions.length > 0 && (
                  <span className="text-sm font-semibold text-brand-orange">
                    {activeSubmissions.length} submission{activeSubmissions.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {/* A2F-993 + A2F-994 + A2F-1035: one ordered list, greyed in
                  place (mirrors Manage Project's "Show hidden" view). Frame it
                  as "which top-level folders are submissions" and explain that
                  Disable excludes a folder via the Ignore rules — the only lever
                  that actually changes ingestion — so what's numbered here is
                  exactly what gets ingested. */}
              <p className="text-xs text-slate-500 leading-relaxed mb-3">
                These are the top-level folders Chrona detected as{' '}
                <span className="font-semibold">submissions</span>.{' '}
                <span className="font-semibold">Disable</span> any that
                aren&rsquo;t — it stays in the list, greyed, and won&rsquo;t be
                ingested. Folders already matching an{' '}
                <span className="font-semibold">Ignore</span> rule (Advanced
                Settings below) are greyed the same way. The numbered ones are
                exactly what gets ingested.
              </p>

              {/* Loading */}
              {loadingFolders && (
                <div className="flex items-center justify-center py-8 text-sm text-slate-400 gap-2">
                  <LoaderCircle className="w-4 h-4 animate-spin" />
                  Discovering folders...
                </div>
              )}

              {/* Empty state — no path */}
              {!loadingFolders && !rootPath.trim() && (
                <div className="py-8 text-center text-sm text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
                  Select a project root folder to discover submissions.
                </div>
              )}

              {/* Empty state — path entered but no folders at all */}
              {!loadingFolders && rootPath.trim() && folders.length === 0 && !error && (
                <div className="py-8 text-center text-sm text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
                  No folders found in the selected directory.
                </div>
              )}

              {/* Empty state — folders exist but every one is excluded. Shown
                  ABOVE the greyed list so the user sees both the warning and the
                  per-folder Enable / Ignore-rule rows. */}
              {!loadingFolders && folders.length > 0 && activeSubmissions.length === 0 && (
                <div className="py-6 px-4 mb-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
                  Every discovered folder is excluded, so nothing would be
                  ingested. Enable a folder below, or edit the Ignore Folders
                  list under Advanced Settings, to include a submission.
                </div>
              )}

              {/* A2F-993/994/1035 — ONE ordered list, greyed in place (mirrors
                  Manage Project's "Show hidden" view). Walk annotatedFolders in
                  discovery order; a local active counter numbers only the active
                  rows "Submission N" (contiguous, descending — newest active =
                  highest N). Excluded rows are greyed with no number and a state
                  badge + action that mirrors Manage Project. Reactive: editing
                  the Ignore list or clicking Disable/Enable re-marks rows live.
                  Drag handlers take the ACTIVE-only index (activeIdx); the drop
                  handler's stable-merge pins excluded rows in place. */}
              {!loadingFolders && annotatedFolders.length > 0 && (
                <div className="space-y-2">
                  {activeSubmissions.length > 0 && (
                    <p className="text-[11px] text-slate-400 flex items-center gap-1.5 mb-2">
                      <span className="inline-block w-3 h-3 text-slate-300">&#x2139;</span>
                      Top = most recent, bottom = oldest. Drag to reorder.
                    </p>
                  )}

                  {(() => {
                    // Local active counter — only active rows advance it, so
                    // numbering stays contiguous over the active subset and the
                    // drag index matches activeSubmissions exactly.
                    let activeIdx = -1;
                    return annotatedFolders.map(({ folder, state }) => {
                      if (state === 'active') {
                        activeIdx += 1;
                        const idx = activeIdx;
                        return (
                          <div
                            key={folder.id}
                            data-testid="fre-folder"
                            data-folder-state="active"
                            draggable
                            onDragStart={() => handleDragStart(idx)}
                            onDragOver={(e) => handleDragOver(e, idx)}
                            onDrop={() => handleDrop(idx)}
                            onDragEnd={handleDragEnd}
                            className={`flex items-center gap-3 px-4 py-3 bg-white border rounded-lg transition-colors ${
                              dragOverIdx === idx
                                ? 'border-brand-orange bg-orange-50/40'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <GripVertical className="w-4 h-4 text-slate-300 shrink-0 cursor-grab" />
                            <Folder className="w-5 h-5 text-brand-charcoal shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-brand-charcoal">
                                Submission {activeSubmissions.length - idx}
                              </p>
                              <p className="text-xs text-slate-400 truncate">
                                {folder.folderName}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDisableFolder(folder.folderName)}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-md transition-colors shrink-0"
                              title="Exclude this folder — it won't be ingested as a submission. (Adds it to Ignore Folders under Advanced Settings; reversible.)"
                            >
                              <Ban className="w-3.5 h-3.5" />
                              Disable
                            </button>
                          </div>
                        );
                      }

                      // Excluded — greyed in place, no number, not draggable.
                      return (
                        <div
                          key={folder.id}
                          data-testid="fre-folder"
                          data-folder-state={state}
                          className="flex items-center gap-3 px-4 py-2.5 bg-slate-50/60 border border-dashed border-slate-200 rounded-lg"
                        >
                          <Folder className="w-5 h-5 text-slate-300 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-400 truncate line-through decoration-slate-300">
                              {folder.folderName}
                            </p>
                          </div>
                          {state === 'disabled' ? (
                            <>
                              {/* User-disabled or an exact seeded default (e.g.
                                  archive) — the Manage-Project is_disabled analog:
                                  Disabled badge + reversible Enable. */}
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-600 shrink-0"
                                title="Excluded — this folder won't be ingested. Click Enable to include it again."
                              >
                                <Ban className="w-3 h-3" />
                                Disabled
                              </span>
                              <button
                                onClick={() => handleEnableFolder(folder.folderName)}
                                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition-colors shrink-0"
                                title="Enable — include this folder again; it will be ingested as a submission."
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Enable
                              </button>
                            </>
                          ) : (
                            // Caught by a wildcard pattern (*pec*) or system core
                            // (.*, ~$*) — the "Removed from source" analog: badge
                            // only, no per-folder toggle.
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-500 shrink-0"
                              title="Matched by a folder rule under Advanced Settings, so ingest will skip it. Edit the Ignore Folders list to include it."
                            >
                              <EyeOff className="w-3 h-3" />
                              Ignored by rule
                            </span>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>

            {/* Advanced Settings (A2F-477 folder rules + A2F-495 file type) */}
            <div className="border border-slate-200 rounded-lg">
              <button
                type="button"
                onClick={() => setRulesExpanded((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50/40 transition-colors rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-brand-orange" />
                  <span className="text-sm font-semibold text-brand-charcoal">Advanced Settings</span>
                </div>
                {rulesExpanded
                  ? <ChevronDown className="w-4 h-4 text-slate-400" />
                  : <ChevronRight className="w-4 h-4 text-slate-400" />}
              </button>
              {rulesExpanded && (
                <div className="px-4 pb-4 pt-4 space-y-5 border-t border-slate-100">
                  {/* Ignore Folders — applied at ingestion (A2F-477 + A2F-1035).
                      Pre-filled with Chrona's recommended defaults (editable);
                      systemCore is the locked, always-on set. These greying out
                      matching top-level folders in the discovery list above. */}
                  <div>
                    <p className="text-xs text-slate-500 leading-relaxed mb-3">
                      Folders to skip when scanning this project — drafts, archives,
                      or working copies that shouldn't be ingested. We&rsquo;ve
                      pre-filled Chrona&rsquo;s recommended defaults; edit or remove
                      any chip, or clear them all to ingest every folder. Matching
                      top-level folders appear greyed out above (clicking{' '}
                      <span className="font-semibold">Disable</span> on a
                      submission adds it here).
                    </p>
                    <FolderTagInput
                      label="Ignore Folders"
                      hint='Skip any folder matching one of these and everything inside it. Case-insensitive: a plain name matches exactly (Drafts); use * for the rest — Final* matches "Final Word Documents", *PeC* matches anywhere.'
                      values={ignoreFolders}
                      onChange={setIgnoreFolders}
                      placeholder="Drafts"
                      lockedValues={systemCore}
                    />
                    {systemCore.length > 0 && (
                      <p className="text-[11px] text-slate-400 mt-1">
                        The muted chips are always ignored by Chrona (system files
                        like dotfolders and Office lock files) — they can&rsquo;t be
                        removed.
                      </p>
                    )}
                  </div>

                  {/* Working Folders — applied at document listing (A2F-656 C3 + A2F-755) */}
                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-xs text-slate-500 leading-relaxed mb-3">
                      Restrict this project to documents inside these folders
                      (e.g. "Word Documents", "Final"). Affects every Chrona
                      feature — See, Review, and Clear. Optional — leave empty
                      to include all folders (default). Opening a specific
                      document by link still works regardless of this setting.
                    </p>
                    <FolderTagInput
                      label="Working Folders"
                      hint='Show only documents inside folders matching one of these. Case-insensitive: a plain name matches exactly (Final); use * for the rest — Final* matches "Final Word Documents", *Final* matches anywhere.'
                      values={activeFolders}
                      onChange={setActiveFolders}
                      placeholder="Word Documents"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
              <button
                onClick={handleSave}
                disabled={!canSave}
                className="flex items-center gap-2 px-6 py-2.5 bg-brand-orange text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-40"
              >
                <Check className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

